import React, { useState, useRef, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { useAuth } from '../AuthContext';
import {
  ALL_WORK_ORDERS_QUERY,
  ALL_USERS_QUERY,
  UPDATE_WORK_ORDER_ASSIGNED_TO_MUTATION,
  UPDATE_WORK_ORDER_STATUS_MUTATION,
  UPDATE_WORK_ORDER_PRIORITY_MUTATION,
} from '../graphql/workOrders';

interface WorkOrder {
  id: number;
  description: string;
  travelerNumber: string | null;
  status: string;
  createdAt: string;
  dueAt: string | null;
  completedAt: string | null;
  priority: number;
  userByAssignedTo: { id: number; username: string } | null;
  quoteByQuoteId: {
    id: number;
    name: string;
    jobByJobId: { id: number; name: string; customerByCustomerId: { id: number; name: string } };
  };
}

interface Assignee {
  id: number;
  username: string;
  role: string;
}

const COLUMNS: { status: string; label: string; accent: string }[] = [
  { status: 'ready', label: 'Ready', accent: '#374151' },
  { status: 'in_progress', label: 'In Progress', accent: '#1e40af' },
  { status: 'on_hold', label: 'On Hold', accent: '#92400e' },
  { status: 'complete', label: 'Complete Today', accent: '#065f46' },
];

function isToday(iso: string | null): boolean {
  if (!iso) return false;
  const d = new Date(iso);
  const now = new Date();
  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
}

const s = {
  page: { padding: '32px 40px', maxWidth: 1400, margin: '0 auto' } as React.CSSProperties,
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 24, flexWrap: 'wrap' as const, gap: 12 } as React.CSSProperties,
  title: { fontSize: 24, fontWeight: 700, margin: 0 } as React.CSSProperties,
  subtitle: { color: '#6b7280', marginTop: 4, fontSize: 14 } as React.CSSProperties,
  filterLabel: { display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 } as React.CSSProperties,
  filterSelect: { border: '1px solid #d1d5db', borderRadius: 6, padding: '7px 10px', fontSize: 13, minWidth: 200, background: '#fff' } as React.CSSProperties,
  board: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 } as React.CSSProperties,
  column: { background: '#f9fafb', borderRadius: 10, padding: 14, minHeight: 320 } as React.CSSProperties,
  count: { background: '#fff', borderRadius: 12, padding: '2px 10px', fontSize: 12, fontWeight: 700, color: '#374151' } as React.CSSProperties,
  card: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px', marginBottom: 10, cursor: 'grab', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' } as React.CSSProperties,
  cardTitle: { fontWeight: 600, fontSize: 13, color: '#111827', marginBottom: 4 } as React.CSSProperties,
  cardMeta: { fontSize: 12, color: '#6b7280', marginBottom: 8 } as React.CSSProperties,
  assignRow: { display: 'flex', alignItems: 'center', gap: 6 } as React.CSSProperties,
  assignSelect: { flex: 1, fontSize: 12, border: '1px solid #d1d5db', borderRadius: 6, padding: '4px 6px', background: '#fff', cursor: 'pointer' } as React.CSSProperties,
  empty: { color: '#9ca3af', fontSize: 13, textAlign: 'center' as const, padding: '24px 0' },
};

export default function ShopStatus() {
  const { user } = useAuth();
  const isWorker = user?.role === 'worker';
  const navigate = useNavigate();
  const [workerFilter, setWorkerFilter] = useState('all');
  const [jobFilter, setJobFilter] = useState('all');
  const [quoteFilter, setQuoteFilter] = useState('all');
  const [reassigningId, setReassigningId] = useState<number | null>(null);
  const [draggedId, setDraggedId] = useState<number | null>(null);
  const [dragOverStatus, setDragOverStatus] = useState<string | null>(null);
  const [movingId, setMovingId] = useState<number | null>(null);
  const [previewOrder, setPreviewOrder] = useState<number[] | null>(null);
  const cardRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const prevRects = useRef<Map<number, DOMRect>>(new Map());
  const suppressFlip = useRef(false);
  const dropHandled = useRef(false);

  const { data, loading, error, refetch } = useQuery(ALL_WORK_ORDERS_QUERY, {
    variables: { domainId: user?.domain_id || 0 },
    skip: !user?.domain_id,
    fetchPolicy: 'network-only',
    pollInterval: 30000,
  });
  const { data: usersData } = useQuery(ALL_USERS_QUERY, {
    variables: { domainId: user?.domain_id || 0 },
    skip: !user?.domain_id,
  });
  const [updateAssignedTo] = useMutation(UPDATE_WORK_ORDER_ASSIGNED_TO_MUTATION);
  const [updateStatus] = useMutation(UPDATE_WORK_ORDER_STATUS_MUTATION);
  const [updatePriority] = useMutation(UPDATE_WORK_ORDER_PRIORITY_MUTATION);

  const assignees: Assignee[] = usersData?.allUsers?.nodes || [];
  const allWorkOrders: WorkOrder[] = data?.allWorkOrders?.nodes || [];

  const roleScoped = isWorker
    ? allWorkOrders.filter((wo) => wo.userByAssignedTo?.id === user?.id)
    : allWorkOrders;

  // Plain lookups built fresh each render — cheap for this list size, and
  // simpler than memoizing derived arrays that change on every query poll.
  const jobsById = new Map<number, string>();
  roleScoped.forEach((wo) => jobsById.set(wo.quoteByQuoteId.jobByJobId.id, wo.quoteByQuoteId.jobByJobId.name));
  const jobs = Array.from(jobsById, ([id, name]) => ({ id, name }));

  const quotesById = new Map<number, { name: string; jobId: number }>();
  roleScoped.forEach((wo) => quotesById.set(wo.quoteByQuoteId.id, { name: wo.quoteByQuoteId.name, jobId: wo.quoteByQuoteId.jobByJobId.id }));
  const quotes = Array.from(quotesById, ([id, v]) => ({ id, name: v.name, jobId: v.jobId }))
    .filter((q) => jobFilter === 'all' || q.jobId === Number(jobFilter));

  const workOrders = roleScoped
    .filter((wo) => isWorker || workerFilter === 'all' || wo.userByAssignedTo?.id === Number(workerFilter))
    .filter((wo) => jobFilter === 'all' || wo.quoteByQuoteId.jobByJobId.id === Number(jobFilter))
    .filter((wo) => quoteFilter === 'all' || wo.quoteByQuoteId.id === Number(quoteFilter));

  const handleJobFilterChange = (value: string) => {
    setJobFilter(value);
    setQuoteFilter('all');
  };

  const handleReassign = async (wo: WorkOrder, newAssignedTo: string) => {
    setReassigningId(wo.id);
    try {
      await updateAssignedTo({ variables: { id: wo.id, assignedTo: newAssignedTo ? Number(newAssignedTo) : null } });
      await refetch();
    } finally {
      setReassigningId(null);
    }
  };

  const assigneeOf = (wo: WorkOrder) => wo.userByAssignedTo?.id ?? null;

  const grouped: Record<string, WorkOrder[]> = { ready: [], in_progress: [], on_hold: [], complete: [] };
  workOrders.forEach((wo) => {
    if (wo.status === 'complete') {
      if (isToday(wo.completedAt)) grouped.complete.push(wo);
    } else if (grouped[wo.status]) {
      grouped[wo.status].push(wo);
    }
  });
  // Ready is manually ranked per employee (lower priority number = higher up
  // the list); other columns keep whatever order the query returned.
  grouped.ready.sort((a, b) => {
    const aAssignee = assigneeOf(a) ?? -1;
    const bAssignee = assigneeOf(b) ?? -1;
    return aAssignee !== bAssignee ? aAssignee - bAssignee : a.priority - b.priority;
  });

  // While actively dragging within Ready, previewOrder overrides the sorted
  // order above so cards visibly shift into their would-be position before
  // the drop actually happens.
  const readyIds = grouped.ready.map((wo) => wo.id);
  const readyOrder =
    previewOrder && previewOrder.length === readyIds.length && previewOrder.every((id) => readyIds.includes(id))
      ? previewOrder
      : readyIds;
  const readyById = new Map(grouped.ready.map((wo) => [wo.id, wo]));
  const displayReady = readyOrder.map((id) => readyById.get(id)).filter((wo): wo is WorkOrder => !!wo);

  // FLIP animation: when a card's position changes between renders (e.g. a
  // reorder bumps it up or down), slide it from its old spot to its new one
  // instead of just snapping there.
  const orderSignature = COLUMNS.map((col) => (col.status === 'ready' ? displayReady : grouped[col.status]).map((wo) => wo.id).join(',')).join('|');
  useLayoutEffect(() => {
    const nextRects = new Map<number, DOMRect>();
    cardRefs.current.forEach((el, id) => nextRects.set(id, el.getBoundingClientRect()));

    // The render where the live preview hands off to the server-confirmed
    // order is a "settle," not a user-driven reorder — skip animating that
    // one so the card just stays exactly where it was dropped.
    if (suppressFlip.current) {
      suppressFlip.current = false;
    } else {
      cardRefs.current.forEach((el, id) => {
        const prev = prevRects.current.get(id);
        const next = nextRects.get(id);
        if (!prev || !next) return;
        const dy = prev.top - next.top;
        if (dy === 0) return;
        el.style.transition = 'none';
        el.style.transform = `translateY(${dy}px)`;
        requestAnimationFrame(() => {
          el.style.transition = 'transform 200ms ease';
          el.style.transform = '';
        });
      });
    }

    prevRects.current = nextRects;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderSignature]);

  // Dragging over a Ready card (same assignee as the dragged one) live-shifts
  // the list — above or below the target depending on which half of the card
  // the cursor is over — so the final order is visible before you let go.
  const handleCardDragOver = (e: React.DragEvent, wo: WorkOrder, columnStatus: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (columnStatus !== 'ready') return;
    const dragged = allWorkOrders.find((w) => w.id === draggedId);
    if (!dragged || dragged.status !== 'ready' || dragged.id === wo.id) return;
    if (assigneeOf(dragged) !== assigneeOf(wo)) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const isAfter = e.clientY > rect.top + rect.height / 2;

    const base = previewOrder ?? readyIds;
    const withoutDragged = base.filter((id) => id !== dragged.id);
    const targetIndex = withoutDragged.indexOf(wo.id);
    if (targetIndex === -1) return;
    const insertAt = isAfter ? targetIndex + 1 : targetIndex;
    const next = [...withoutDragged.slice(0, insertAt), dragged.id, ...withoutDragged.slice(insertAt)];

    setPreviewOrder((prev) => ((prev ?? readyIds).join(',') === next.join(',') ? prev : next));
  };

  // Commits whatever order the live preview built up (if any); otherwise
  // falls back to a plain status change. The preview is kept in place (not
  // cleared) until the server confirms the same order, so the list doesn't
  // snap back to the old order and then snap again once refetch resolves —
  // it just drops directly where you released it.
  const handleDrop = async (targetStatus: string) => {
    // dragend fires on the source element right after this, but before this
    // async function's first await resolves — mark the drop as handled
    // synchronously so dragend knows not to clear the preview out from under
    // the in-flight mutation below.
    dropHandled.current = true;
    const dragged = allWorkOrders.find((w) => w.id === draggedId);
    const finalPreview = previewOrder;
    setDragOverStatus(null);
    setDraggedId(null);
    if (!dragged) { setPreviewOrder(null); return; }

    if (targetStatus === 'ready' && dragged.status === 'ready' && finalPreview) {
      const group = finalPreview
        .map((id) => allWorkOrders.find((w) => w.id === id))
        .filter((wo): wo is WorkOrder => !!wo && assigneeOf(wo) === assigneeOf(dragged));
      setMovingId(dragged.id);
      suppressFlip.current = true;
      try {
        await Promise.all(group.map((wo, index) => updatePriority({ variables: { id: wo.id, priority: index } })));
        await refetch();
      } finally {
        setMovingId(null);
        setPreviewOrder(null);
        // Safety net: if the settled order exactly matches the preview, the
        // order signature never changes, so the effect above never runs to
        // consume the flag. Clear it directly so the *next* drag isn't
        // wrongly suppressed too.
        setTimeout(() => { suppressFlip.current = false; }, 0);
      }
      return;
    }

    setPreviewOrder(null);
    if (dragged.status === targetStatus) return;
    setMovingId(dragged.id);
    try {
      await updateStatus({ variables: { id: dragged.id, status: targetStatus } });
      await refetch();
    } finally {
      setMovingId(null);
    }
  };

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div>
          <h1 style={s.title}>Shop Status</h1>
          <p style={s.subtitle}>{isWorker ? 'Your work orders' : 'Where every work order stands right now'}</p>
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <label style={s.filterLabel}>Filter by Job</label>
            <select style={s.filterSelect} value={jobFilter} onChange={(e) => handleJobFilterChange(e.target.value)}>
              <option value="all">All Jobs</option>
              {jobs.map((j) => (
                <option key={j.id} value={j.id}>{j.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={s.filterLabel}>Filter by Quote</label>
            <select style={s.filterSelect} value={quoteFilter} onChange={(e) => setQuoteFilter(e.target.value)}>
              <option value="all">All Quotes</option>
              {quotes.map((q) => (
                <option key={q.id} value={q.id}>{q.name}</option>
              ))}
            </select>
          </div>
          {!isWorker && (
            <div>
              <label style={s.filterLabel}>Filter by Assignee</label>
              <select style={s.filterSelect} value={workerFilter} onChange={(e) => setWorkerFilter(e.target.value)}>
                <option value="all">Everyone</option>
                {assignees.map((a) => (
                  <option key={a.id} value={a.id}>{a.username}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {error && <div style={{ color: '#dc2626', marginBottom: 16 }}>Error: {error.message}</div>}

      {loading && workOrders.length === 0 ? (
        <div style={s.empty}>Loading...</div>
      ) : (
        <div style={s.board}>
          {COLUMNS.map((col) => {
            const list = col.status === 'ready' ? displayReady : grouped[col.status];
            return (
            <div
              key={col.status}
              style={{
                ...s.column,
                background: dragOverStatus === col.status ? '#eef2ff' : s.column.background,
                outline: dragOverStatus === col.status ? `2px dashed ${col.accent}` : 'none',
              }}
              onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOverStatus(col.status); }}
              onDragLeave={() => setDragOverStatus((prev) => (prev === col.status ? null : prev))}
              onDrop={(e) => { e.preventDefault(); handleDrop(col.status); }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, paddingBottom: 8, borderBottom: `2px solid ${col.accent}` }}>
                <span style={{ fontWeight: 700, fontSize: 14, color: col.accent }}>{col.label}</span>
                <span style={s.count}>{list.length}</span>
              </div>
              {list.length === 0 ? (
                <div style={s.empty}>Drop here</div>
              ) : (
                list.map((wo) => (
                  <div
                    key={wo.id}
                    ref={(el) => { if (el) cardRefs.current.set(wo.id, el); else cardRefs.current.delete(wo.id); }}
                    draggable
                    onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; setDraggedId(wo.id); }}
                    onDragEnd={() => {
                      setDraggedId(null);
                      // Only reset the preview here if the drag was aborted
                      // (dropped outside any valid target) — a real drop is
                      // already being handled by handleDrop, which owns
                      // clearing the preview on its own timing.
                      if (!dropHandled.current) setPreviewOrder(null);
                      dropHandled.current = false;
                    }}
                    onDragOver={(e) => handleCardDragOver(e, wo, col.status)}
                    onDrop={(e) => { e.preventDefault(); e.stopPropagation(); handleDrop(col.status); }}
                    style={{ ...s.card, opacity: draggedId === wo.id || movingId === wo.id ? 0.4 : 1 }}
                    onClick={() => navigate(`/work-orders/${wo.id}`, { state: { from: '/shop-status' } })}
                  >
                    <div style={s.cardTitle}>{wo.description}</div>
                    <div style={s.cardMeta}>{wo.quoteByQuoteId.jobByJobId.customerByCustomerId.name}</div>
                    {isWorker ? (
                      <div style={s.cardMeta}>{wo.userByAssignedTo?.username || 'Unassigned'}</div>
                    ) : (
                      <div style={s.assignRow} onClick={(e) => e.stopPropagation()}>
                        <select
                          style={s.assignSelect}
                          value={wo.userByAssignedTo?.id ?? ''}
                          disabled={reassigningId === wo.id}
                          onChange={(e) => handleReassign(wo, e.target.value)}
                        >
                          <option value="">Unassigned</option>
                          {assignees.map((a) => (
                            <option key={a.id} value={a.id}>{a.username}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
